/**
 * Raportet – forma me logo; kWh ditore + total; check-in/checkout (sesione); naftë; PDF/CSV.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

import { BACKEND_URL } from '../config';

const logoSrc = () => `${process.env.PUBLIC_URL || ''}/assets/images/logo-dio-iot.png`;

/** Fillim / fund muaji në datë lokale YYYY-MM-DD */
function monthRangeLocal(which) {
  const now = new Date();
  let y = now.getFullYear();
  let m = now.getMonth();
  if (which === 'previous') {
    if (m === 0) {
      y -= 1;
      m = 11;
    } else {
      m -= 1;
    }
  }
  const first = new Date(y, m, 1);
  const last = new Date(y, m + 1, 0);
  const pad = (n) => String(n).padStart(2, '0');
  const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return { from: fmt(first), to: fmt(last) };
}

function eachDayLocal(fromStr, toStr) {
  const out = [];
  const [fy, fm, fd] = fromStr.split('-').map(Number);
  const [ty, tm, td] = toStr.split('-').map(Number);
  let d = new Date(fy, fm - 1, fd);
  const end = new Date(ty, tm - 1, td);
  while (d <= end) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
  }
  return out;
}

/** Rreshta ditore: kWh, check-in/checkout, naftë/produkt, DIN0/DIN1 orë ON, vlerësim nafte nga gjenerator (jo për VIEWER). */
function buildGeneralDailyRows(dateFromStr, dateToStr, checkinList, dailyKwhArray, dinDailyArray, options) {
  const days = eachDayLocal(dateFromStr, dateToStr);
  const kwhByDay = new Map();
  (dailyKwhArray || []).forEach((row) => {
    if (row && row.date != null) kwhByDay.set(row.date, row.kwh ?? 0);
  });
  const dinByDay = new Map();
  (dinDailyArray || []).forEach((row) => {
    if (row && row.date != null) dinByDay.set(row.date, row);
  });
  const list = Array.isArray(checkinList) ? checkinList : [];
  const track = String(options?.generator_tracking_din || 'DIN0').toUpperCase();
  const lph = Number(options?.generator_liters_per_hour);
  const useLph = Number.isFinite(lph) && lph > 0;
  const hideDinEst = options?.hideDinEstColumns === true;
  let cumLoaded = 0;
  let cumEst = 0;
  return days.map((day) => {
    const rowEvents = list.filter((c) => (c.created_at || '').slice(0, 10) === day);
    let checkins = 0;
    let checkouts = 0;
    let fuelSum = 0;
    let partsSum = 0;
    rowEvents.forEach((c) => {
      const ek = String(c.event_kind || 'checkin').toLowerCase();
      if (ek === 'checkout') checkouts += 1;
      else checkins += 1;
      const fl = parseFloat(String(c.fuel_liters ?? '').replace(',', '.'));
      if (!Number.isNaN(fl)) fuelSum += fl;
      const pc = Number(c.parts_count);
      if (Number.isFinite(pc)) partsSum += Math.round(pc);
    });
    const kwh = kwhByDay.has(day) ? kwhByDay.get(day) : null;
    const dinRow = dinByDay.get(day);
    const h0 = dinRow != null && typeof dinRow.din0_hours_on === 'number' ? dinRow.din0_hours_on : null;
    const h1 = dinRow != null && typeof dinRow.din1_hours_on === 'number' ? dinRow.din1_hours_on : null;
    const genH = track === 'DIN1' ? h1 : h0;
    let estLiters = null;
    if (!hideDinEst && useLph && genH != null) {
      estLiters = Math.round(genH * lph * 1000) / 1000;
    }
    const loaded = fuelSum > 0 ? Math.round(fuelSum * 1000) / 1000 : null;
    if (loaded) cumLoaded += loaded;
    if (estLiters != null) cumEst += estLiters;
    const remaining = !hideDinEst && (cumLoaded > 0 || cumEst > 0) ? Math.round((cumLoaded - cumEst) * 1000) / 1000 : null;
    return {
      date: day,
      kwh,
      checkins,
      checkouts,
      fuel_liters: loaded,
      parts_count: partsSum > 0 ? partsSum : null,
      din0_hours_on: hideDinEst ? null : h0,
      din1_hours_on: hideDinEst ? null : h1,
      gen_tracked_hours: hideDinEst ? null : genH,
      est_liters: hideDinEst ? null : estLiters,
      cumulative_remaining_liters: hideDinEst ? null : remaining,
    };
  });
}

/** Çift check-in → checkout në një rresht (rendit sipas kohës). */
function buildCheckinSessionRows(events) {
  const list = Array.isArray(events) ? events : [];
  const sorted = [...list].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const rows = [];
  let openIn = null;
  for (const ev of sorted) {
    const kind = String(ev.event_kind || 'checkin').toLowerCase();
    if (kind === 'checkout') {
      if (openIn) {
        rows.push({
          key: `${openIn.id}-${ev.id}`,
          location_name: ev.location_name || openIn.location_name,
          location_id: ev.location_id || openIn.location_id,
          inAt: openIn.created_at,
          outAt: ev.created_at,
        });
        openIn = null;
      } else {
        rows.push({
          key: `out-${ev.id}`,
          location_name: ev.location_name || '—',
          location_id: ev.location_id,
          inAt: null,
          outAt: ev.created_at,
        });
      }
    } else {
      if (openIn) {
        rows.push({
          key: `in-${openIn.id}-open`,
          location_name: openIn.location_name,
          location_id: openIn.location_id,
          inAt: openIn.created_at,
          outAt: null,
        });
      }
      openIn = ev;
    }
  }
  if (openIn) {
    rows.push({
      key: `in-${openIn.id}`,
      location_name: openIn.location_name,
      location_id: openIn.location_id,
      inAt: openIn.created_at,
      outAt: null,
    });
  }
  rows.sort((a, b) => {
    const ta = new Date(a.outAt || a.inAt).getTime();
    const tb = new Date(b.outAt || b.inAt).getTime();
    return tb - ta;
  });
  return rows;
}

const Reports = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SUPERADMIN';
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportType, setReportType] = useState('general');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [checkinData, setCheckinData] = useState([]);
  const [checkinSummary, setCheckinSummary] = useState(null);
  const [fuelRows, setFuelRows] = useState([]);
  const [kwhData, setKwhData] = useState(null);
  const [inputsData, setInputsData] = useState(null);
  const [generalRows, setGeneralRows] = useState([]);
  const [generalKwhBlock, setGeneralKwhBlock] = useState(null);
  const [lastFetchedType, setLastFetchedType] = useState(null);

  const getToken = () => localStorage.getItem('access_token');
  const headers = () => ({ Authorization: `Bearer ${getToken()}` });

  const setDefaultMonth = useCallback(() => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setDateFrom(first.toISOString().split('T')[0]);
    setDateTo(last.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/s475/devices`, { headers: headers() });
        const list = Array.isArray(res.data) ? res.data : [];
        setDevices(list);
      } catch (e) {
        setDevices([]);
      } finally {
        setLoading(false);
      }
    };
    fetchDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!dateFrom || !dateTo) setDefaultMonth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setDefaultMonth]);

  useEffect(() => {
    if (!isSuperAdmin && (reportType === 'checkin' || reportType === 'fuel')) {
      setReportType('general');
    }
  }, [isSuperAdmin, reportType]);

  const fetchReport = async (rangeOverride) => {
    const df = rangeOverride?.dateFrom ?? dateFrom;
    const dt = rangeOverride?.dateTo ?? dateTo;
    if (!df || !dt) {
      toast.error(t('selectDateRange'));
      return;
    }
    if (rangeOverride?.dateFrom != null) setDateFrom(rangeOverride.dateFrom);
    if (rangeOverride?.dateTo != null) setDateTo(rangeOverride.dateTo);
    setLoadingReport(true);
    setCheckinData([]);
    setCheckinSummary(null);
    setFuelRows([]);
    setKwhData(null);
    setInputsData(null);
    setGeneralRows([]);
    setGeneralKwhBlock(null);
    if ((reportType === 'checkin' || reportType === 'fuel') && !isSuperAdmin) {
      toast.error(t('reportsSuperAdminOnly'));
      setLoadingReport(false);
      return;
    }
    const paramsBase = new URLSearchParams({ date_from: df, date_to: dt });
    if (deviceId) paramsBase.set('location_id', deviceId);
    if (isSuperAdmin && (reportType === 'checkin' || reportType === 'fuel')) {
      paramsBase.set('report_scope', 'reporter');
    }
    try {
      if (reportType === 'checkin') {
        const [listRes, sumRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/checkins?${paramsBase}`, { headers: headers() }),
          axios.get(`${BACKEND_URL}/api/checkins/summary?${paramsBase}`, { headers: headers() }).catch(() => ({ data: null })),
        ]);
        setCheckinData(Array.isArray(listRes.data) ? listRes.data : []);
        setCheckinSummary(sumRes.data && typeof sumRes.data === 'object' ? sumRes.data : null);
        setLastFetchedType('checkin');
      } else if (reportType === 'fuel') {
        const res = await axios.get(`${BACKEND_URL}/api/checkins?${paramsBase}`, { headers: headers() });
        const list = Array.isArray(res.data) ? res.data : [];
        const filtered = list.filter(
          (c) =>
            (c.fuel_liters != null && c.fuel_liters !== '') ||
            (c.parts_count != null && c.parts_count !== '') ||
            (c.work_comment && String(c.work_comment).trim())
        );
        setFuelRows(filtered);
        setLastFetchedType('fuel');
      } else if (reportType === 'kwh') {
        const params = new URLSearchParams({ date_from: df, date_to: dt });
        if (deviceId) params.set('device_id', deviceId);
        const res = await axios.get(`${BACKEND_URL}/api/reports/kwh?${params}`, { headers: headers() });
        setKwhData(res.data);
        setLastFetchedType('kwh');
      } else if (reportType === 'inputs' || reportType === 'inputeve') {
        const params = new URLSearchParams({ date_from: df, date_to: dt });
        if (deviceId) params.set('device_id', deviceId);
        const res = await axios.get(`${BACKEND_URL}/api/reports/inputs?${params}`, { headers: headers() });
        setInputsData(res.data);
        setLastFetchedType('inputs');
      } else if (reportType === 'general') {
        const [listRes, sumRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/checkins?${paramsBase}`, { headers: headers() }),
          axios.get(`${BACKEND_URL}/api/checkins/summary?${paramsBase}`, { headers: headers() }).catch(() => ({ data: null })),
        ]);
        const list = Array.isArray(listRes.data) ? listRes.data : [];
        setCheckinSummary(sumRes.data && typeof sumRes.data === 'object' ? sumRes.data : null);
        let daily = [];
        let kwhBlock = null;
        let dinDaily = [];
        const dev = devices.find((d) => String(d.id) === String(deviceId));
        if (deviceId) {
          try {
            const kparams = new URLSearchParams({ date_from: df, date_to: dt, device_id: deviceId });
            const kres = await axios.get(`${BACKEND_URL}/api/reports/kwh?${kparams}`, { headers: headers() });
            kwhBlock = kres.data;
            daily = Array.isArray(kwhBlock?.daily) ? kwhBlock.daily : [];
          } catch {
            kwhBlock = { message: t('generalKwhLoadFailed'), daily: [], total_kwh: null };
          }
          try {
            const dparams = new URLSearchParams({ date_from: df, date_to: dt, device_id: deviceId });
            const dres = await axios.get(`${BACKEND_URL}/api/reports/din-daily?${dparams}`, { headers: headers() });
            dinDaily = Array.isArray(dres.data?.daily) ? dres.data.daily : [];
          } catch {
            dinDaily = [];
          }
        }
        setGeneralKwhBlock(kwhBlock);
        setGeneralRows(
          buildGeneralDailyRows(df, dt, list, daily, dinDaily, {
            generator_liters_per_hour: dev?.generator_liters_per_hour,
            generator_tracking_din: dev?.generator_tracking_din || 'DIN0',
            hideDinEstColumns: user?.role === 'VIEWER',
          })
        );
        setLastFetchedType('general');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || t('error'));
    } finally {
      setLoadingReport(false);
    }
  };

  const formatDateOra = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      const h = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      return `${day}.${month}.${year} ${h}:${min}`;
    } catch (_) {
      return iso;
    }
  };

  const formatDaySq = (isoDate) => {
    if (!isoDate) return '—';
    try {
      const [y, m, d] = isoDate.split('-').map(Number);
      return `${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}.${y}`;
    } catch {
      return isoDate;
    }
  };

  const formatTimeOnly = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      const h = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      return `${h}:${min}`;
    } catch {
      return '—';
    }
  };

  const checkinSessionRows = useMemo(() => buildCheckinSessionRows(checkinData), [checkinData]);
  const checkinTotalWorkHours = useMemo(() => {
    let sum = 0;
    checkinSessionRows.forEach((r) => {
      if (r.inAt && r.outAt) {
        const ms = new Date(r.outAt) - new Date(r.inAt);
        if (ms > 0) sum += ms / (1000 * 60 * 60);
      }
    });
    return Math.round(sum * 100) / 100;
  }, [checkinSessionRows]);

  const formatSessionHours = (inAt, outAt) => {
    if (!inAt || !outAt) return '—';
    const ms = new Date(outAt) - new Date(inAt);
    if (ms <= 0) return '—';
    const h = ms / (1000 * 60 * 60);
    const rounded = Math.round(h * 100) / 100;
    return `${rounded.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} h`;
  };

  const generalDinDevice = devices.find((d) => String(d.id) === String(deviceId));
  const din0Label =
    generalDinDevice?.dashboard_channel_labels?.DIN0 ||
    generalDinDevice?.dashboard_channel_labels?.din0 ||
    'DIN0';
  const din1Label =
    generalDinDevice?.dashboard_channel_labels?.DIN1 ||
    generalDinDevice?.dashboard_channel_labels?.din1 ||
    'DIN1';
  const showGeneralDinCols = user?.role !== 'VIEWER';

  const downloadCheckinPDF = () => {
    const sessions = buildCheckinSessionRows(checkinData);
    const tableRows = sessions
      .map((r) => {
        const hrs = formatSessionHours(r.inAt, r.outAt);
        const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
        return `<tr><td>${esc(r.location_name || r.location_id || '—')}</td><td>${esc(r.inAt ? formatDateOra(r.inAt) : '—')}</td><td>${esc(r.outAt ? formatDateOra(r.outAt) : '—')}</td><td style="text-align:right">${esc(hrs)}</td></tr>`;
      })
      .join('');
    const totalH = checkinTotalWorkHours.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    const logo = logoSrc();
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Raporti check-in ${dateFrom} - ${dateTo}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 24px; }
          .hdr { display: flex; align-items: center; gap: 16px; margin-bottom: 20px; }
          .hdr img { height: 48px; }
          h1 { color: #1e40af; border-bottom: 2px solid #1e40af; padding-bottom: 8px; font-size: 18px; }
          .meta { color: #666; font-size: 12px; margin-bottom: 16px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
          th { background: #f3f4f6; font-weight: 600; }
          .footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #ddd; color: #666; font-size: 11px; }
          .total-row td { font-weight: bold; background: #f9fafb; }
        </style>
      </head>
      <body>
        <div class="hdr"><img src="${logo}" alt="" onerror="this.style.display='none'" /></div>
        <div class="meta">Raporti check-in / checkout | ${dateFrom} deri ${dateTo}${deviceId ? ' | Pajisje e zgjedhur' : ' | Të gjitha pajisjet'}</div>
        ${checkinSummary != null ? `<p class="meta"><strong>${t('checkinCountLabel')}:</strong> ${checkinSummary.checkins ?? 0} &nbsp; <strong>${t('checkoutCountLabel')}:</strong> ${checkinSummary.checkouts ?? 0}</p>` : ''}
        <h1>Raporti</h1>
        <table>
          <thead><tr><th>${t('locationLabel')}</th><th>${t('reportSessionIn')}</th><th>${t('reportSessionOut')}</th><th style="text-align:right">${t('reportWorkHoursColumn')}</th></tr></thead>
          <tbody>${tableRows}<tr class="total-row"><td colspan="3">${t('reportTotalWorkHours')}</td><td style="text-align:right">${totalH} h</td></tr></tbody>
        </table>
        <div class="footer">© ${new Date().getFullYear()}</div>
      </body>
      </html>
    `;
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.print();
  };

  const downloadFuelCsv = () => {
    const bom = '\uFEFF';
    const head = ['Lloji', 'Lokacioni', 'Data', 'Naftë (L)', 'Copë', 'Koment'];
    const lines = [head, ...fuelRows.map((c) => [
      (c.event_kind || 'checkin') === 'checkout' ? 'Checkout' : 'Check-in',
      c.location_name || c.location_id || '',
      formatDateOra(c.created_at),
      c.fuel_liters ?? '',
      c.parts_count ?? '',
      (c.work_comment || '').replace(/"/g, '""'),
    ])];
    const csv = bom + lines.map((r) => r.map((x) => `"${String(x)}"`).join(';')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `raport-nafte-${dateFrom}-${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const downloadKwhCsv = () => {
    if (!kwhData) return;
    const bom = '\uFEFF';
    const daily = Array.isArray(kwhData.daily) ? kwhData.daily : [];
    const lines = [[t('dayColumn'), t('kwhColumn')], ...daily.map((d) => [formatDaySq(d.date), d.kwh ?? 0]), [t('totalKwh'), kwhData.total_kwh ?? 0]];
    const csv = bom + lines.map((r) => r.join(';')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `raport-kwh-${dateFrom}-${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const downloadGeneralCsv = () => {
    if (!generalRows.length) return;
    const bom = '\uFEFF';
    const head = [
      t('dayColumn'),
      t('kwhColumn'),
      t('generalColCheckin'),
      t('generalColCheckout'),
      t('fuelLitersColumn'),
      t('partsColumn'),
      ...(showGeneralDinCols ? [din0Label, din1Label, t('generalColTrackedGenHours'), t('generalColEstLiters'), t('generalColRemainingLiters')] : []),
    ];
    const lines = [
      head,
      ...generalRows.map((r) => [
        formatDaySq(r.date),
        r.kwh != null ? r.kwh : '',
        r.checkins,
        r.checkouts,
        r.fuel_liters ?? '',
        r.parts_count ?? '',
        ...(showGeneralDinCols
          ? [
              r.din0_hours_on != null ? r.din0_hours_on : '',
              r.din1_hours_on != null ? r.din1_hours_on : '',
              r.gen_tracked_hours != null ? r.gen_tracked_hours : '',
              r.est_liters != null ? r.est_liters : '',
              r.cumulative_remaining_liters != null ? r.cumulative_remaining_liters : '',
            ]
          : []),
      ]),
    ];
    const csv = bom + lines.map((r) => r.map((x) => `"${String(x)}"`).join(';')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `raport-i-pergjithshem-${dateFrom}-${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const downloadGeneralPdf = () => {
    if (!generalRows.length) return;
    const extra = showGeneralDinCols
      ? (r) =>
          `<td style="text-align:right">${r.din0_hours_on != null ? r.din0_hours_on : '—'}</td><td style="text-align:right">${r.din1_hours_on != null ? r.din1_hours_on : '—'}</td><td style="text-align:right">${r.gen_tracked_hours != null ? r.gen_tracked_hours : '—'}</td><td style="text-align:right">${r.est_liters != null ? r.est_liters : '—'}</td><td style="text-align:right">${r.cumulative_remaining_liters != null ? r.cumulative_remaining_liters : '—'}</td>`
      : () => '';
    const tableRows = generalRows
      .map((r) => {
        const mid = `<tr><td>${formatDaySq(r.date)}</td><td style="text-align:right">${r.kwh != null ? (r.kwh ?? 0).toLocaleString() : '—'}</td><td style="text-align:center">${r.checkins}</td><td style="text-align:center">${r.checkouts}</td><td style="text-align:right">${r.fuel_liters ?? '—'}</td><td style="text-align:right">${r.parts_count ?? '—'}</td>`;
        return `${mid}${showGeneralDinCols ? extra(r) : ''}</tr>`;
      })
      .join('');
    const logo = logoSrc();
    const totalLine =
      generalKwhBlock && generalKwhBlock.total_kwh != null
        ? `<p class="total">${t('totalKwh')}: ${(generalKwhBlock.total_kwh ?? 0).toLocaleString()} kWh</p>`
        : '';
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>${t('reportGeneral')} ${dateFrom} - ${dateTo}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 24px; }
          .hdr { display: flex; align-items: center; gap: 16px; margin-bottom: 16px; }
          .hdr img { height: 48px; }
          h1 { color: #1e40af; font-size: 18px; }
          table { width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 11px; }
          th, td { border: 1px solid #ddd; padding: 6px; }
          th { background: #f3f4f6; }
          .total { margin-top: 12px; text-align: right; font-weight: bold; }
          .meta { color: #666; font-size: 12px; margin-bottom: 8px; }
        </style>
      </head>
      <body>
        <div class="hdr"><img src="${logo}" alt="" onerror="this.style.display='none'" /></div>
        <div class="meta">${dateFrom} – ${dateTo}${deviceId ? '' : ''}</div>
        <h1>${t('reportGeneral')}</h1>
        ${checkinSummary != null ? `<p class="meta">${t('checkinCountLabel')}: ${checkinSummary.checkins ?? 0} · ${t('checkoutCountLabel')}: ${checkinSummary.checkouts ?? 0}</p>` : ''}
        <table>
          <thead><tr><th>${t('dayColumn')}</th><th>${t('kwhColumn')}</th><th>${t('generalColCheckin')}</th><th>${t('generalColCheckout')}</th><th>${t('fuelLitersColumn')}</th><th>${t('partsColumn')}</th>${showGeneralDinCols ? `<th>${din0Label}</th><th>${din1Label}</th><th>${t('generalColTrackedGenHours')}</th><th>${t('generalColEstLiters')}</th><th>${t('generalColRemainingLiters')}</th>` : ''}</tr></thead>
          <tbody>${tableRows}</tbody>
        </table>
        ${totalLine}
      </body>
      </html>
    `;
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.print();
  };

  const downloadKwhPdf = () => {
    if (!kwhData) return;
    const daily = Array.isArray(kwhData.daily) ? kwhData.daily : [];
    const tableRows = daily
      .map((d) => `<tr><td>${formatDaySq(d.date)}</td><td style="text-align:right">${(d.kwh ?? 0).toLocaleString()}</td></tr>`)
      .join('');
    const logo = logoSrc();
    const total = (kwhData.total_kwh ?? 0).toLocaleString();
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Raporti kWh ${dateFrom} - ${dateTo}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 24px; }
          .hdr { display: flex; align-items: center; gap: 16px; margin-bottom: 16px; }
          .hdr img { height: 48px; }
          h1 { color: #1e40af; font-size: 18px; }
          table { width: 100%; border-collapse: collapse; margin-top: 12px; }
          th, td { border: 1px solid #ddd; padding: 8px; }
          th { background: #f3f4f6; }
          .total { margin-top: 16px; text-align: right; font-size: 16px; font-weight: bold; }
          .msg { color: #92400e; font-size: 12px; margin-top: 8px; }
        </style>
      </head>
      <body>
        <div class="hdr"><img src="${logo}" alt="" onerror="this.style.display='none'" /><div><span style="font-size:12px;color:#666">Raporti kWh — ${kwhData.period?.date_from} – ${kwhData.period?.date_to}</span></div></div>
        <h1>${t('reportKwh')}</h1>
        ${kwhData.message ? `<p class="msg">${kwhData.message}</p>` : ''}
        <table>
          <thead><tr><th>${t('dayColumn')}</th><th>${t('kwhColumn')}</th></tr></thead>
          <tbody>${tableRows}</tbody>
        </table>
        <p class="total">${t('totalKwh')}: ${total} kWh</p>
      </body>
      </html>
    `;
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.print();
  };

  const reportTypeOptions = useMemo(() => {
    const all = [
      { value: 'checkin', labelKey: 'checkinReport' },
      { value: 'fuel', labelKey: 'reportFuel' },
      { value: 'general', labelKey: 'reportGeneral' },
      { value: 'kwh', labelKey: 'reportKwh' },
      { value: 'inputeve', labelKey: 'reportInputeve' },
    ];
    if (isSuperAdmin) return all;
    return all.filter((o) => o.value !== 'checkin' && o.value !== 'fuel');
  }, [isSuperAdmin]);

  return (
    <Layout>
      <div className="p-4 md:p-6">
        <div className="mb-6">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">{t('reportsTitle') || 'Raportet'}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('reportsSubtitle') || 'Zgjidhni llojin e raportit, periudhën dhe pajisjen.'}</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
            <img
              src={logoSrc()}
              alt=""
              className="h-10 md:h-12 object-contain dark:hidden"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `${process.env.PUBLIC_URL || ''}/assets/images/logo-full.svg`;
              }}
            />
            <img
              src={`${process.env.PUBLIC_URL || ''}/assets/images/logo-dio-iot-white.png`}
              alt=""
              className="h-10 md:h-12 object-contain hidden dark:block"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `${process.env.PUBLIC_URL || ''}/assets/images/logo-white-full.svg`;
              }}
            />
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('reportType')}</label>
              <select
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                className="w-full md:max-w-xs px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                {reportTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('fromDate')}</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('toDate')}</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('deviceLabel')}</label>
              <select
                value={deviceId}
                onChange={(e) => setDeviceId(e.target.value)}
                className="w-full md:max-w-xs px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                disabled={loading}
              >
                <option value="">{t('allDevicesFilter')}</option>
                {devices.map((d) => (
                  <option key={d.id} value={d.id}>{d.location_name || d.label || d.id}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-row flex-wrap items-stretch gap-2">
              <button
                type="button"
                onClick={() => fetchReport()}
                disabled={loadingReport}
                className="flex-1 min-w-0 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
              >
                {loadingReport ? t('loading') : t('viewReport')}
              </button>
              <button
                type="button"
                disabled={loadingReport}
                onClick={() => {
                  const { from, to } = monthRangeLocal('previous');
                  fetchReport({ dateFrom: from, dateTo: to });
                }}
                className="shrink-0 px-2.5 py-2.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 whitespace-nowrap"
              >
                {t('reportMonthPrevious')}
              </button>
              <button
                type="button"
                disabled={loadingReport}
                onClick={() => {
                  const { from, to } = monthRangeLocal('current');
                  fetchReport({ dateFrom: from, dateTo: to });
                }}
                className="shrink-0 px-2.5 py-2.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 whitespace-nowrap"
              >
                {t('reportMonthCurrent')}
              </button>
            </div>
          </div>
        </div>

        {reportType === 'checkin' && lastFetchedType === 'checkin' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('checkinReport')}</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{dateFrom} – {dateTo}</p>
                {checkinSummary && (
                  <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">
                    <span className="font-medium text-cyan-700 dark:text-cyan-400">{t('checkinCountLabel')}: {checkinSummary.checkins ?? 0}</span>
                    {' · '}
                    <span className="font-medium text-amber-700 dark:text-amber-400">{t('checkoutCountLabel')}: {checkinSummary.checkouts ?? 0}</span>
                  </p>
                )}
              </div>
              {checkinSessionRows.length > 0 && (
                <button
                  type="button"
                  onClick={downloadCheckinPDF}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                  {t('downloadPdf')}
                </button>
              )}
            </div>
            <div className="overflow-x-auto">
              {checkinSessionRows.length === 0 ? (
                <div className="p-6 text-center text-gray-500 dark:text-gray-400">{t('noCheckinDataForPeriod')}</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-700/50">
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">{t('locationLabel')}</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">{t('reportSessionIn')}</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">{t('reportSessionOut')}</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-white">{t('reportWorkHoursColumn')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {checkinSessionRows.map((r) => (
                      <tr key={r.key} className="border-t border-gray-200 dark:border-gray-700">
                        <td className="px-4 py-3 text-gray-900 dark:text-white">{r.location_name || r.location_id || '—'}</td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{r.inAt ? formatDateOra(r.inAt) : '—'}</td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{r.outAt ? formatDateOra(r.outAt) : '—'}</td>
                        <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300 tabular-nums">
                          {formatSessionHours(r.inAt, r.outAt)}
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/40 font-semibold">
                      <td className="px-4 py-3 text-gray-900 dark:text-white" colSpan={3}>
                        {t('reportTotalWorkHours')}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-900 dark:text-white tabular-nums">
                        {checkinTotalWorkHours.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} h
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {reportType === 'fuel' && lastFetchedType === 'fuel' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap justify-between gap-2">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('reportFuel')}</h2>
              {fuelRows.length > 0 && (
                <button
                  type="button"
                  onClick={downloadFuelCsv}
                  className="px-3 py-2 text-sm font-medium text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg"
                >
                  {t('downloadExcel')}
                </button>
              )}
            </div>
            <div className="overflow-x-auto">
              {fuelRows.length === 0 ? (
                <div className="p-6 text-center text-gray-500 dark:text-gray-400">{t('noFuelReportData')}</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-700/50">
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">{t('locationLabel')}</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">{t('dateAndTime')}</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-white">{t('fuelLitersColumn')}</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-white">{t('partsColumn')}</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">{t('commentColumn')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fuelRows.map((c) => (
                      <tr key={c.id} className="border-t border-gray-200 dark:border-gray-700">
                        <td className="px-4 py-3 text-gray-900 dark:text-white">{c.location_name || c.location_id}</td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{formatDateOra(c.created_at)}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{c.fuel_liters ?? '—'}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{c.parts_count ?? '—'}</td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400 max-w-xs truncate" title={c.work_comment || ''}>{c.work_comment || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {reportType === 'general' && lastFetchedType === 'general' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('reportGeneral')}</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{dateFrom} – {dateTo}</p>
                {checkinSummary && (
                  <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">
                    <span className="font-medium text-cyan-700 dark:text-cyan-400">{t('checkinCountLabel')}: {checkinSummary.checkins ?? 0}</span>
                    {' · '}
                    <span className="font-medium text-amber-700 dark:text-amber-400">{t('checkoutCountLabel')}: {checkinSummary.checkouts ?? 0}</span>
                  </p>
                )}
                {!deviceId && (
                  <p className="text-xs text-amber-700 dark:text-amber-400 mt-2">{t('generalReportPickDeviceKwh')}</p>
                )}
              </div>
              {generalRows.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={downloadGeneralPdf}
                    className="px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                  >
                    {t('downloadPdf')}
                  </button>
                  <button
                    type="button"
                    onClick={downloadGeneralCsv}
                    className="px-3 py-2 text-sm font-medium text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg"
                  >
                    {t('downloadExcel')}
                  </button>
                </div>
              )}
            </div>
            <div className="overflow-x-auto p-2 md:p-4">
              {generalRows.length === 0 ? (
                <div className="p-6 text-center text-gray-500 dark:text-gray-400">{t('noCheckinDataForPeriod')}</div>
              ) : (
                <table className="w-full text-sm min-w-[640px]">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-700/50">
                      <th className="px-3 py-2 text-left font-semibold text-gray-900 dark:text-white">{t('dayColumn')}</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-900 dark:text-white">{t('kwhColumn')}</th>
                      <th className="px-3 py-2 text-center font-semibold text-gray-900 dark:text-white">{t('generalColCheckin')}</th>
                      <th className="px-3 py-2 text-center font-semibold text-gray-900 dark:text-white">{t('generalColCheckout')}</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-900 dark:text-white">{t('fuelLitersColumn')}</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-900 dark:text-white">{t('partsColumn')}</th>
                      {showGeneralDinCols && (
                        <>
                          <th className="px-3 py-2 text-right font-semibold text-gray-900 dark:text-white">{din0Label}</th>
                          <th className="px-3 py-2 text-right font-semibold text-gray-900 dark:text-white">{din1Label}</th>
                          <th className="px-3 py-2 text-right font-semibold text-gray-900 dark:text-white">{t('generalColTrackedGenHours')}</th>
                          <th className="px-3 py-2 text-right font-semibold text-gray-900 dark:text-white">{t('generalColEstLiters')}</th>
                          <th className="px-3 py-2 text-right font-semibold text-gray-900 dark:text-white">{t('generalColRemainingLiters')}</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {generalRows.map((r) => (
                      <tr key={r.date} className="border-t border-gray-200 dark:border-gray-700">
                        <td className="px-3 py-2 text-gray-900 dark:text-white">{formatDaySq(r.date)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-900 dark:text-white">{r.kwh != null ? (r.kwh ?? 0).toLocaleString() : '—'}</td>
                        <td className="px-3 py-2 text-center tabular-nums">{r.checkins}</td>
                        <td className="px-3 py-2 text-center tabular-nums">{r.checkouts}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-900 dark:text-white">{r.fuel_liters ?? '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-900 dark:text-white">{r.parts_count ?? '—'}</td>
                        {showGeneralDinCols && (
                          <>
                            <td className="px-3 py-2 text-right tabular-nums text-gray-900 dark:text-white">{r.din0_hours_on != null ? r.din0_hours_on : '—'}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-gray-900 dark:text-white">{r.din1_hours_on != null ? r.din1_hours_on : '—'}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-gray-900 dark:text-white">{r.gen_tracked_hours != null ? r.gen_tracked_hours : '—'}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-gray-900 dark:text-white">{r.est_liters != null ? r.est_liters : '—'}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-gray-900 dark:text-white">{r.cumulative_remaining_liters != null ? r.cumulative_remaining_liters : '—'}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {generalKwhBlock && generalKwhBlock.total_kwh != null && (
                <div className="flex justify-end pt-3 mt-2 border-t border-gray-200 dark:border-gray-600">
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {t('totalKwh')}: {(generalKwhBlock.total_kwh || 0).toLocaleString()} kWh
                  </p>
                </div>
              )}
              {generalKwhBlock?.message && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">{generalKwhBlock.message}</p>
              )}
            </div>
          </div>
        )}

        {reportType === 'kwh' && lastFetchedType === 'kwh' && kwhData && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('reportKwh')}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{kwhData.period?.date_from} – {kwhData.period?.date_to}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={downloadKwhPdf}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                >
                  {t('downloadPdf')}
                </button>
                <button
                  type="button"
                  onClick={downloadKwhCsv}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg"
                >
                  {t('downloadExcel')}
                </button>
              </div>
            </div>
            <div className="p-4">
              {kwhData.message && <p className="text-amber-600 dark:text-amber-400 text-sm mb-3">{kwhData.message}</p>}
              {Array.isArray(kwhData.daily) && kwhData.daily.length > 0 ? (
                <div className="overflow-x-auto mb-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-700/50">
                        <th className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-white">{t('dayColumn')}</th>
                        <th className="px-4 py-2 text-right font-semibold text-gray-900 dark:text-white">{t('kwhColumn')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kwhData.daily.map((d) => (
                        <tr key={d.date} className="border-t border-gray-200 dark:border-gray-700">
                          <td className="px-4 py-2 text-gray-900 dark:text-white">{formatDaySq(d.date)}</td>
                          <td className="px-4 py-2 text-right font-medium tabular-nums text-gray-900 dark:text-white">{(d.kwh ?? 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{t('noDataStoredPeriod')}</p>
              )}
              <div className="flex justify-end pt-2 border-t border-gray-200 dark:border-gray-600">
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {t('totalKwh')}: {(kwhData.total_kwh || 0).toLocaleString()} kWh
                </p>
              </div>
            </div>
          </div>
        )}

        {(reportType === 'inputs' || reportType === 'inputeve') && lastFetchedType === 'inputs' && inputsData && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('reportInputsTitle')}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{inputsData.period?.date_from} – {inputsData.period?.date_to}</p>
              {inputsData.message && (
                <p className="text-sm text-amber-600 dark:text-amber-400 mt-2">{inputsData.message}</p>
              )}
              {inputsData.sample_count != null && inputsData.sample_count >= 0 && (
                <p className="text-xs text-gray-400 mt-1">
                  {inputsData.sample_count} {inputsData.sample_count === 1 ? 'lexim' : 'lexime'} në periudhë (PostgreSQL)
                </p>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700/50">
                    <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">{t('inputColumn')}</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">{t('minutesOn')}</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">{t('hoursOn')}</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">{t('minutesOff')}</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">{t('hoursOff')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(inputsData.inputs || []).map((inp) => (
                    <tr key={inp.key} className="border-t border-gray-200 dark:border-gray-700">
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                        {inp.label || inp.key}
                        {inp.label && inp.key ? (
                          <span className="block text-xs font-normal text-gray-500 dark:text-gray-400">{inp.key}</span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{inp.minutes_on ?? 0}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{(inp.hours_on ?? 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{inp.minutes_off ?? 0}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{(inp.hours_off ?? 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!inputsData.inputs || inputsData.inputs.length === 0) && (
                <div className="p-4 text-sm text-gray-500 dark:text-gray-400">{t('noInputsDataMessage')}</div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Reports;
